import React, { useState, useEffect } from "react"
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material"
import {
  LocalShipping,
  Inventory,
  TrackChanges,
  Warning,
  CheckCircle,
} from "@mui/icons-material"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import { apiService } from "../services/apiService"
import LoadingSkeleton from "./LoadingSkeleton"
import StatusBadge from "./StatusBadge"

interface DashboardData {
  shipments: {
    total: number
    active: number
    delivered: number
    cancelled: number
    inTransit: number
    outForDelivery: number
    averageTransitTime: number
    onTimeDeliveryRate: number
    totalValue: number
  }
  inventory: {
    locations: Array<{
      id: string
      name: string
      type: string
      address: string
      capacity: number
      utilization: number
      totalSkus: number
      totalValue: number
    }>
    summary: {
      totalLocations: number
      totalSkus: number
      totalValue: number
      averageUtilization: number
      lowStockAlerts: number
      movementsToday: number
    }
    topSkus: Array<{
      sku: string
      totalQuantity: number
      totalValue: number
      locations: number
    }>
    recentMovements: Array<{
      id: string
      sku: string
      type: string
      quantity: number
      location: string
      fromLocation?: string
      toLocation?: string
      timestamp: string
    }>
  }
  tracking: {
    activeShipments: Array<{
      shipmentId: string
      shipmentNumber: string
      status: string
      currentLocation: {
        latitude: number
        longitude: number
        address: string
        facilityCode: string
      }
      lastUpdated: string
      estimatedDelivery: string
      carrier: string
      carrierReference: string
    }>
    summary: {
      totalActiveShipments: number
      inTransit: number
      outForDelivery: number
      atFacility: number
      deliveredToday: number
      exceptionsToday: number
      onTimeDeliveryRate: number
      averageTransitTime: number
    }
    recentEvents: Array<{
      id: string
      shipmentId: string
      eventType: string
      status: string
      location: string
      timestamp: string
      source: string
    }>
    performanceMetrics: {
      averageLocationUpdateFrequency: number
      dataAccuracy: number
      systemUptime: number
      totalEvents: number
    }
  }
}

const Overview: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dashboardData = await apiService.getDashboardData()
        setData(dashboardData)
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    const interval = setInterval(fetchData, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [])

  if (loading) {
    return <LoadingSkeleton type="dashboard" />
  }

  if (!data) {
    return (
      <Typography variant="h6" color="error">
        Failed to load dashboard data
      </Typography>
    )
  }

  const performanceData = [
    { name: "Mon", deliveries: 45, onTime: 42 },
    { name: "Tue", deliveries: 52, onTime: 48 },
    { name: "Wed", deliveries: 38, onTime: 36 },
    { name: "Thu", deliveries: 61, onTime: 58 },
    { name: "Fri", deliveries: 55, onTime: 52 },
    { name: "Sat", deliveries: 28, onTime: 26 },
    { name: "Sun", deliveries: 22, onTime: 21 },
  ]

  const statusData = [
    {
      name: "In Transit",
      value: data.tracking.summary.inTransit,
      color: "#1976d2",
      percentage: (
        (data.tracking.summary.inTransit /
          data.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "Out for Delivery",
      value: data.tracking.summary.outForDelivery,
      color: "#ed6c02",
      percentage: (
        (data.tracking.summary.outForDelivery /
          data.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "At Facility",
      value: data.tracking.summary.atFacility,
      color: "#2e7d32",
      percentage: (
        (data.tracking.summary.atFacility /
          data.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "Delivered Today",
      value: data.tracking.summary.deliveredToday,
      color: "#388e3c",
      percentage: (
        (data.tracking.summary.deliveredToday /
          data.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "Exceptions",
      value: data.tracking.summary.exceptionsToday,
      color: "#d32f2f",
      percentage: (
        (data.tracking.summary.exceptionsToday /
          data.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
  ]

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
          }}
        >
          Dashboard Overview
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ fontWeight: 400 }}
        >
          Real-time insights into your logistics operations
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: "1px solid rgba(26, 35, 126, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "linear-gradient(90deg, #1a237e 0%, #534bae 100%)",
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Active Shipments
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: "#1a237e",
                      lineHeight: 1.2,
                    }}
                  >
                    {data.shipments.active}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LocalShipping sx={{ fontSize: 32 }} />
                </Box>
              </Box>
              <Box>
                <Chip
                  label={`${data.shipments.inTransit} in transit`}
                  size="small"
                  sx={{
                    background:
                      "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: "1px solid rgba(16, 185, 129, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Inventory Value
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: "#10b981",
                      lineHeight: 1.2,
                    }}
                  >
                    ${(data.inventory.summary.totalValue / 1000000).toFixed(1)}M
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Inventory sx={{ fontSize: 32 }} />
                </Box>
              </Box>
              <Box>
                <Chip
                  label={`${data.inventory.summary.totalSkus} SKUs`}
                  size="small"
                  sx={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: "1px solid rgba(16, 185, 129, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    On-Time Delivery
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: "#10b981",
                      lineHeight: 1.2,
                    }}
                  >
                    {(data.shipments.onTimeDeliveryRate * 100).toFixed(1)}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle sx={{ fontSize: 32 }} />
                </Box>
              </Box>
              <Box>
                <Chip
                  label={`${data.tracking.summary.deliveredToday} today`}
                  size="small"
                  sx={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border:
                data.tracking.summary.exceptionsToday > 0
                  ? "1px solid rgba(239, 68, 68, 0.1)"
                  : "1px solid rgba(16, 185, 129, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background:
                  data.tracking.summary.exceptionsToday > 0
                    ? "linear-gradient(90deg, #ef4444 0%, #f87171 100%)"
                    : "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Exceptions
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color:
                        data.tracking.summary.exceptionsToday > 0
                          ? "#ef4444"
                          : "#10b981",
                      lineHeight: 1.2,
                    }}
                  >
                    {data.tracking.summary.exceptionsToday}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background:
                      data.tracking.summary.exceptionsToday > 0
                        ? "linear-gradient(135deg, #ef4444 0%, #f87171 100%)"
                        : "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Warning sx={{ fontSize: 32 }} />
                </Box>
              </Box>
              <Box>
                <Chip
                  label={`${data.inventory.summary.lowStockAlerts} low stock`}
                  size="small"
                  sx={{
                    background:
                      data.tracking.summary.exceptionsToday > 0
                        ? "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)"
                        : "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Delivery Performance (Last 7 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="deliveries"
                    stroke="#1976d2"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="onTime"
                    stroke="#2e7d32"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Shipment Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Shipments
              </Typography>
              <List>
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon>
                    <LocalShipping color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="SH-2025-001"
                    secondary="Chicago → Los Angeles"
                    sx={{ flex: 1 }}
                  />
                  <StatusBadge status="In Transit" size="small" />
                </ListItem>
                <Divider />
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="SH-2025-002"
                    secondary="New York → Miami"
                    sx={{ flex: 1 }}
                  />
                  <StatusBadge status="Delivered" size="small" />
                </ListItem>
                <Divider />
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon>
                    <TrackChanges color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="SH-2025-003"
                    secondary="Seattle → Portland"
                    sx={{ flex: 1 }}
                  />
                  <StatusBadge status="At Facility" size="small" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Health
              </Typography>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">API Gateway</Typography>
                  <Typography variant="body2" color="success.main">
                    99.9%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.9}
                  color="success"
                />
              </Box>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Shipment Service</Typography>
                  <Typography variant="body2" color="success.main">
                    99.8%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.8}
                  color="success"
                />
              </Box>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Inventory Service</Typography>
                  <Typography variant="body2" color="success.main">
                    99.9%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.9}
                  color="success"
                />
              </Box>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Tracking Service</Typography>
                  <Typography variant="body2" color="success.main">
                    99.7%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.7}
                  color="success"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Overview
